import React, { useEffect, useState, useRef } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';
import { DateRangePicker } from './DateRangePicker';
export type DateRangeOption = 'today' | '7days' | '30days' | '90days' | 'year' | 'custom';
interface DateRangeTabsProps {
  value: DateRangeOption;
  onChange: (value: DateRangeOption) => void;
  customStartDate?: string;
  customEndDate?: string;
  onCustomDateChange?: (startDate: string, endDate: string) => void;
  className?: string;
}
export function DateRangeTabs({
  value,
  onChange,
  customStartDate = '',
  customEndDate = '',
  onCustomDateChange,
  className = ''
}: DateRangeTabsProps) {
  const [showPopover, setShowPopover] = useState(false);
  const [tempStartDate, setTempStartDate] = useState(customStartDate);
  const [tempEndDate, setTempEndDate] = useState(customEndDate);
  const customButtonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const tabs: {
    id: DateRangeOption;
    label: string;
  }[] = [{
    id: 'today',
    label: 'Today'
  }, {
    id: '7days',
    label: '7 Days'
  }, {
    id: '30days',
    label: '30 Days'
  }, {
    id: '90days',
    label: '90 Days'
  }, {
    id: 'year',
    label: 'This Year'
  }];
  // Close popover when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node) && customButtonRef.current && !customButtonRef.current.contains(event.target as Node)) {
        setShowPopover(false);
      }
    }
    if (showPopover) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showPopover]);
  // Update temp dates when custom dates change
  useEffect(() => {
    setTempStartDate(customStartDate);
    setTempEndDate(customEndDate);
  }, [customStartDate, customEndDate]);
  const handleCustomClick = () => {
    setShowPopover(!showPopover);
    if (!showPopover) {
      onChange('custom');
    }
  };
  const handleApply = () => {
    if (onCustomDateChange && tempStartDate && tempEndDate) {
      onCustomDateChange(tempStartDate, tempEndDate);
    }
    setShowPopover(false);
  };
  const handleCancel = () => {
    setTempStartDate(customStartDate);
    setTempEndDate(customEndDate);
    setShowPopover(false);
  };
  // Format custom date range for display
  const formatCustomRange = () => {
    if (!customStartDate || !customEndDate) return 'Custom';
    const formatDate = (dateStr: string) => {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    };
    return `${formatDate(customStartDate)} - ${formatDate(customEndDate)}`;
  };
  return <div className={`w-full ${className}`} data-id="element-1353">
      {/* Mobile View: Dropdown */}
      <div className="md:hidden w-full" data-id="element-1354">
        <div className="relative" data-id="element-1355">
          <select value={value} onChange={e => onChange(e.target.value as DateRangeOption)} className="w-full h-10 pl-3 pr-10 bg-white border border-[#E2E8F0] rounded-lg text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent appearance-none shadow-sm" data-id="element-1356">
            {tabs.map(tab => <option key={tab.id} value={tab.id} data-id="element-1357">
                {tab.label}
              </option>)}
            <option value="custom" data-id="element-1358">
              {customStartDate && customEndDate ? formatCustomRange() : 'Custom Range...'}
            </option>
          </select>
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none" data-id="element-1359">
            <ChevronDown className="h-4 w-4 text-[#64748B]" data-id="element-1360" />
          </div>
        </div>
      </div>

      {/* Desktop View: Unified Tab Row */}
      <div className="hidden md:block relative" data-id="element-1361">
        

        {/* Custom Date Picker Popover */}
        {showPopover && <div ref={popoverRef} className="absolute top-full left-0 mt-2 z-50 bg-white rounded-lg border border-[#E2E8F0] shadow-lg animate-in fade-in slide-in-from-top-2 duration-200" style={{
        minWidth: '320px'
      }} data-id="element-1362">
            {/* Header */}
            <div className="px-4 py-3 border-b border-[#E2E8F0]" data-id="element-1363">
              <h3 className="text-sm font-semibold text-[#0F172A]" data-id="element-1364">
                Select Date Range
              </h3>
            </div>

            {/* Body */}
            <div className="p-4 space-y-4" data-id="element-1365">
              {/* Start Date */}
              <div data-id="element-1366">
                <label className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-1367">
                  Start Date
                </label>
                <div className="relative" data-id="element-1368">
                  <input type="date" value={tempStartDate} onChange={e => setTempStartDate(e.target.value)} className="w-full h-10 pl-3 pr-10 bg-white border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent" data-id="element-1369" />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none" data-id="element-1370">
                    <Calendar className="h-4 w-4 text-[#64748B]" data-id="element-1371" />
                  </div>
                </div>
              </div>

              {/* End Date */}
              <div data-id="element-1372">
                <label className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-1373">
                  End Date
                </label>
                <div className="relative" data-id="element-1374">
                  <input type="date" value={tempEndDate} onChange={e => setTempEndDate(e.target.value)} className="w-full h-10 pl-3 pr-10 bg-white border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent" data-id="element-1375" />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none" data-id="element-1376">
                    <Calendar className="h-4 w-4 text-[#64748B]" data-id="element-1377" />
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-4 py-3 border-t border-[#E2E8F0] flex justify-end gap-2" data-id="element-1378">
              <button onClick={handleCancel} className="px-4 py-2 text-[14px] font-medium text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9] rounded-md transition-colors" data-id="element-1379">
                Cancel
              </button>
              <button onClick={handleApply} disabled={!tempStartDate || !tempEndDate} className="px-4 py-2 h-10 text-[14px] font-medium text-white bg-[#0EA5E9] hover:bg-[#0284C7] rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1380">
                Apply
              </button>
            </div>
          </div>}
      </div>
    </div>;
}